import { ApplicationShell, FrontendApplication, FrontendApplicationContribution } from '@theia/core/lib/browser';
import { inject, injectable } from '@theia/core/shared/inversify';
import { EditorManager, EditorWidget } from '@theia/editor/lib/browser';
import { WorkspaceService } from '@theia/workspace/lib/browser/workspace-service';
import { showTheiaTopPanel } from './application-shell';

/** Body class added when the custom macOS title bar is active. */
export const MAC_TITLEBAR_BODY_CLASS = 'lvgl-mac-titlebar';

/**
 * Injects a custom title bar into the Theia top panel when running on
 * macOS inside Electron (where `titleBarStyle: "hiddenInset"` hides the
 * native OS title bar text but keeps the traffic-light buttons).
 *
 * The bar shows:  <workspace folder name> — <active file name.ext>
 *
 * On browser, Linux, and Windows the native / Theia menu bar occupies the
 * top row, so this contribution does nothing on those platforms.
 *
 * Implementation note: ElectronMenuContribution.hideTopPanel() calls
 * Widget.setHidden(true) on the top panel during onStart(). We restore it
 * in onDidInitializeLayout() (which runs after all onStart contributions)
 * by calling Widget.show() so Lumino recalculates the BoxLayout and pushes
 * lrPanel down by the panel's actual height.
 */
@injectable()
export class MacTitlebarContribution implements FrontendApplicationContribution {
    @inject(ApplicationShell)
    protected readonly shell!: ApplicationShell;

    @inject(WorkspaceService)
    protected readonly workspaceService!: WorkspaceService;

    @inject(EditorManager)
    protected readonly editorManager!: EditorManager;

    private textEl: HTMLSpanElement | undefined;

    async onDidInitializeLayout(_app: FrontendApplication): Promise<void> {
        if (!this.isMacElectron()) return;

        document.body.classList.add(MAC_TITLEBAR_BODY_CLASS);

        // Restore the top panel via Lumino's own Widget.show() so the
        // BoxLayout recalculates and lrPanel is properly pushed down.
        // This must run after all onStart() contributions (including
        // ElectronMenuContribution which hides it).
        showTheiaTopPanel(this.shell);

        const topPanelNode = document.getElementById('theia-top-panel');
        if (!topPanelNode) return;

        const text = document.createElement('span');
        text.id = 'lvgl-mac-titlebar-text';
        topPanelNode.appendChild(text);
        this.textEl = text;

        this.editorManager.onCurrentEditorChanged((editor: EditorWidget | undefined) => {
            this.updateTitle(editor);
        });
        this.updateTitle(this.editorManager.currentEditor);
    }

    /** Returns true only on macOS inside Electron. */
    private isMacElectron(): boolean {
        return 'electronTheiaCore' in window && /Mac/i.test(navigator.userAgent);
    }

    private updateTitle(editor: EditorWidget | undefined): void {
        if (!this.textEl) return;

        const roots = this.workspaceService.tryGetRoots();
        const workspaceName = roots[0]?.resource.path.base ?? '';
        const fileName = editor?.getResourceUri()?.path.base ?? '';

        if (workspaceName && fileName) {
            this.textEl.textContent = `${workspaceName} \u2014 ${fileName}`;
        } else if (workspaceName) {
            this.textEl.textContent = workspaceName;
        } else {
            this.textEl.textContent = fileName;
        }
    }
}
